// 채팅 첨부 이미지 전처리 — 리사이즈·재인코딩 후 base64로
//
// 첨부 이미지는 두 경로를 함께 탄다.
//   1) 채팅 요청 본문(images 배열)에 원본을 실어 보낸다 — 여기서 만드는 축소본이 쓰인다.
//      모델이 이미지를 직접 보므로 세밀한 시각 질문에 답할 수 있다.
//   2) /api/ingest/file로 원본 File을 그대로 색인한다(utils 밖, attachFiles).
//      비전 모델이 전사한 텍스트가 영구 기억·검색을 담당한다.
// 1번은 base64가 원본의 약 1.37배인 데다 여러 턴에 걸쳐 다시 전송되므로,
// 원본 그대로 보내지 않고 반드시 축소·재인코딩한다. 2번은 전사 정확도를 위해 원본을 쓴다.

/** 채팅 요청에 실을 이미지 — data는 헤더를 뗀 순수 base64 */
export interface ChatImage {
  uid: string;
  name: string;
  mimeType: string;
  data: string;
  /** 화면 미리보기용 objectURL (전송에는 쓰지 않음) */
  previewUrl: string;
}

/** 서버가 수용하는 형식 — 그 외는 첨부를 거절한다 (GIF는 첫 프레임만 전달됨) */
const ACCEPTED = ['image/png', 'image/jpeg', 'image/webp', 'image/gif'];

export function isSupportedImage(file: File): boolean {
  return ACCEPTED.includes(file.type);
}

export function isImageFile(file: File): boolean {
  return file.type.startsWith('image/');
}

/* 장변 기준 축소 배율 — 이미 작으면 확대하지 않는다 */
function fitSize(width: number, height: number, maxEdge: number) {
  const longest = Math.max(width, height);
  if (longest <= maxEdge) return { width, height };
  const scale = maxEdge / longest;
  return { width: Math.round(width * scale), height: Math.round(height * scale) };
}

function toBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    // readAsDataURL 결과는 "data:<mime>;base64,<payload>" — 서버엔 payload만 보낸다
    reader.onload = () => {
      const result = String(reader.result);
      const comma = result.indexOf(',');
      resolve(comma >= 0 ? result.slice(comma + 1) : result);
    };
    reader.onerror = () => reject(new Error('이미지를 읽지 못했습니다'));
    reader.readAsDataURL(blob);
  });
}

/** canvas.toBlob의 Promise 판 — 인코딩 실패 시 null */
function encode(canvas: HTMLCanvasElement, mimeType: string, quality: number): Promise<Blob | null> {
  return new Promise(resolve => canvas.toBlob(resolve, mimeType, quality));
}

let imageUidSeq = 0;

/**
 * 첨부 이미지를 전송 가능한 형태로 변환한다.
 * WebP로 재인코딩 — 알파를 보존하면서 PNG보다 훨씬 작다. 인코딩이 안 되는 환경에서는
 * JPEG로 물러선다(이 경우 투명 영역은 흰색으로 채운다).
 */
export async function prepareChatImage(file: File, maxEdge = 1280): Promise<ChatImage> {
  const previewUrl = URL.createObjectURL(file);
  const bitmap = await createImageBitmap(file);
  try {
    const { width, height } = fitSize(bitmap.width, bitmap.height, maxEdge);
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;

    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('이미지를 변환하지 못했습니다');
    ctx.drawImage(bitmap, 0, 0, width, height);

    let mimeType = 'image/webp';
    let blob = await encode(canvas, mimeType, 0.85);
    // toBlob은 지원하지 않는 형식이면 PNG로 대체해버리므로 실제 타입까지 확인한다
    if (!blob || blob.type !== mimeType) {
      mimeType = 'image/jpeg';
      // JPEG는 알파가 없다 — 투명 배경이 검게 나오지 않도록 흰색을 깔고 다시 그린다
      ctx.globalCompositeOperation = 'destination-over';
      ctx.fillStyle = '#FFFFFF';
      ctx.fillRect(0, 0, width, height);
      blob = await encode(canvas, mimeType, 0.85);
    }
    if (!blob) throw new Error('이미지를 변환하지 못했습니다');

    return {
      uid: `img-${++imageUidSeq}`,
      name: file.name,
      mimeType,
      data: await toBase64(blob),
      previewUrl,
    };
  } catch (e) {
    URL.revokeObjectURL(previewUrl); // 변환에 실패했으면 미리보기도 쓰이지 않는다
    throw e;
  } finally {
    bitmap.close();
  }
}
